import React from 'react'

import {
  AlignItems,
  Body3,
  Box,
  Color,
  Display,
  Heading4,
  Join,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { RecipeIngredients as ExperienceRecipeIngredients } from '@library/api-menu-experience'

import Image from 'components/Image'
import Svg from 'components/Svg'
import imageCss from 'routes/Menu/components/Detail/Ingredients/IngredientImage.css'
import css from 'routes/Menu/components/Detail/Ingredients/RecipeIngredients.css'
import { RecipeAttributePill } from 'routes/Menu/components/RecipeAttributePill/RecipeAttributePill'
import { getMenuRecipeImage } from 'utils/image'

type RecipeIngredientsProps = {
  ingredients?: ExperienceRecipeIngredients
  loading: boolean
}

const LoadingSkeleton = ({ style }: { style?: React.CSSProperties }) => (
  <span className={css.loadingSkeleton} style={style} data-testid="LoadingSkeleton" />
)

const SectionPanel = withCitrusTheme(Box, (theme) => ({
  marginBottom: theme.spacing(4),
  backgroundColor: theme.color(Color.White),
}))

const IngredientRow = ({
  text,
  imageCrops,
}: {
  text: string
  imageCrops: Array<{ url: string; width: number }>
}) => {
  const imageUrl = imageCrops.length > 0 ? getMenuRecipeImage(imageCrops, 125) : null

  return (
    <Box display={Display.Flex} alignItems={AlignItems.Center}>
      {imageUrl ? (
        <Image media={imageUrl} title={text} className={imageCss.image} />
      ) : (
        <Svg fileName="icon-vegs" className={imageCss.placeholder} />
      )}
      <Space size={2} direction="horizontal" />
      <Body3 data-testid="ingredient-label">{text}</Body3>
    </Box>
  )
}

export const RecipeIngredients = ({ ingredients, loading }: RecipeIngredientsProps) => {
  const includedIngredients = ingredients?.included || []
  const notIncludedIngredients = ingredients?.notIncluded || []

  return (
    <SectionPanel data-testing="recipeDetailsIngredients">
      <Box>
        <Box>
          <Join with={<Space size={2} />}>
            <Heading4>{ingredients?.title || 'Ingredients'}</Heading4>
            <Body3>For allergens see the &apos;Ingredients contain&apos; section.</Body3>
          </Join>
        </Box>
        <Box paddingV={3}>
          <Join with={<Space size={loading ? 4 : 2} />}>
            {loading
              ? [...Array(6)].map((_, index) => (
                  <LoadingSkeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={`ingredient-loading-${index}`}
                    style={{ height: '10px', width: '200px' }}
                  />
                ))
              : includedIngredients.map((ingredient) => (
                  <IngredientRow
                    key={ingredient.text}
                    text={ingredient.text}
                    imageCrops={ingredient.image.crops}
                  />
                ))}
          </Join>
        </Box>
        <Box>
          <Join with={<Space size={2} />}>
            <Heading4>Ingredients (or dietary alternatives) not included:</Heading4>
            {loading ? (
              <Box>
                {[...Array(4)].map((_, index) => (
                  <LoadingSkeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={`not-included-loading-${index}`}
                    style={{
                      width: '90px',
                      height: '30px',
                      marginBottom: '10px',
                      marginRight: '5px',
                      borderRadius: '40px',
                    }}
                  />
                ))}
              </Box>
            ) : (
              notIncludedIngredients.length > 0 && (
                <Box alignItems={AlignItems.FlexStart} wrap display={Display.Flex}>
                  {notIncludedIngredients.map((item) => (
                    <RecipeAttributePill key={item.text} text={item.text} />
                  ))}
                </Box>
              )
            )}
          </Join>
        </Box>
      </Box>
    </SectionPanel>
  )
}
